import { createSelector } from 'reselect';

import { AUTH_CHANNEL_STATUS } from 'constants/authChannel';
import {
  COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW,
  GRACE_EXPIRY_DAYS,
  IS_DEMO,
  IS_INDIAN_EXCHANGE,
} from 'constants/constants';
import { NRI_STATUS_VALUES, TRADE_PAGE_VERIFICATION_FLAGS } from 'constants/enums';
import { GRACE_KYC_EXPIRY_AVAILABLE } from 'constants/featureFlag';
import { OCCUPATION as SOURCE_INCOME_OCCUPATION } from 'constants/incomeSource';
import { DEPOSIT_BLOCKED_COUNTRIES_GLOBAL_EXCHANGE, OCCUPATION } from 'constants/user';
import { getDateTime, timeNow } from 'helpers/day';
import { isOldUser } from 'helpers/kycStatus';
import { shouldDisplayKycCard } from 'helpers/user';
import {
  isKycRefreshExpired as checkKycRefreshExpired,
  isKycRequiredDeadlineCrossed,
  isUserNonIndianAndReduceOnly,
  isUserVerified,
} from 'helpers/utils';
import IState from 'types/Istore';

import {
  isUserIndianSelector,
  isUserKycDoneSelector,
  userAddressProofSelector,
  userProfileSelector,
  userSelector,
} from './otherSelectors';

const SOURCE_INCOME_OCCUPATION_LIST = Object.values(SOURCE_INCOME_OCCUPATION);

const selectUserReferralCode = createSelector([userSelector], user => user.referral_code);

const selectIsUserSubAccount = createSelector([userSelector], user =>
  Boolean(user.is_sub_account)
);

const selectIsVerifiedIndianUser = createSelector(
  [isUserIndianSelector, isUserKycDoneSelector],
  (isIndian, isKycDone) => isIndian && isKycDone
);

const selectUserAccountName = createSelector([userSelector], user => user.account_name);

const selectMfaCheckForSubAndMainAccount = createSelector([userSelector], user => {
  if (user.is_sub_account) {
    if (user.main_account?.email) {
      return user.main_account?.isMfaEnabled;
    }
  }
  return user.isMfaEnabled;
});

const selectActiveSubAccountEmailEqualsMainAccountEmail = createSelector(
  [userSelector],
  user => {
    if (user.is_sub_account) {
      return user.main_account?.email === user.email;
    }
    return false;
  }
);

/**
 * @description This will give the referral code used during signup process of the user.
 * It can be some custom referral code or the default referral code.
 */
const selectSignupReferralCode = createSelector([userProfileSelector], profile => {
  return profile?.signup_referral_code;
});

const selectUserPreference = (state: IState) => state.user.preferences;

// Whether the current user is a normal user or not
const isNormalUserSelector = (state: IState) => state.user.profile?.is_normal_user;

const isUserLevelOneVerifiedSelector = createSelector(
  [userSelector],
  user => user.phone_verification_status === 'verified'
);

const isUserLevelTwoVerifiedSelector = createSelector(
  [userSelector],
  user => user.proof_of_identity_status === 'approved'
);

const isUserLevelThreeVerifiedSelector = createSelector(
  [userSelector],
  user => user.proof_of_address_status === 'approved'
);

const onlyLevel2VerifiedSelector = createSelector(
  [isUserLevelTwoVerifiedSelector, isUserLevelThreeVerifiedSelector],
  (levelTwoVerified, levelThreeVerified) => levelTwoVerified && !levelThreeVerified
);
const selectUserPhoneVerificationStatus = (state: IState) =>
  state.user.phone_verification_status;

// When user is reactivated after  deletion of account and phone is unverified, we should ask the user to verify the phone
const selectShouldAskReActivatedUserToVerifyPhone = createSelector(
  [isUserKycDoneSelector, selectUserPhoneVerificationStatus, selectIsUserSubAccount],
  (isKycDone, phoneVerificationStatus, isSubAccount) => {
    if (isSubAccount) {
      return false;
    }
    return Boolean(isKycDone) && phoneVerificationStatus === 'unverified';
  }
);

const selectUserGlobalKycData = (state: IState) => state.user.userGlobalKycData;

const selectShowDoKycCard = createSelector([userSelector], user => {
  const { profile, is_kyc_done: isKycDone, kycShareConsentPopupConfig } = user;
  const { login_count: loginCount } = profile ?? {};

  if (kycShareConsentPopupConfig?.show) {
    return false;
  }

  return Boolean(shouldDisplayKycCard(isKycDone, loginCount));
});

const selectUserEmailSha256 = (state: IState) => state.user.sha256EmailHexString;

const selectIsKycRefreshRequired = (state: IState) => {
  return state.user.is_kyc_refresh_required;
};

const selectKycExpiryDate = (state: IState) => state.user.kyc_expiry_date;

const selectKycRefreshProcessPending = createSelector(
  [isUserKycDoneSelector, userAddressProofSelector, selectIsKycRefreshRequired],
  (isKycDone, proofOfAddress, isKycRefreshRequired) => {
    return !isKycDone && isKycRefreshRequired;
  }
);

export const isUserVerifiedSelector = createSelector(
  [userSelector, isUserKycDoneSelector, selectIsKycRefreshRequired, selectKycExpiryDate],
  (user, isKycDone, isKycRefreshRequired, kycExpiryDate) => {
    if (IS_DEMO) return true;

    if (IS_INDIAN_EXCHANGE) {
      return isUserVerified({
        is_kyc_done: isKycDone,
        is_kyc_refresh_required: isKycRefreshRequired,
        kyc_expiry_date: kycExpiryDate,
      });
    }

    const {
      country,
      phone_verification_status: phoneVerificationStatus,
      proof_of_identity_status: proofOfIdentityStatus,
      proof_of_address_status: proofOfAddressStatus,
    } = user;
    const isPhoneVerified = phoneVerificationStatus === 'verified';
    const proofOfIdentityApproved = proofOfIdentityStatus === 'approved';
    const proofOfAddressApproved = proofOfAddressStatus === 'approved';

    if (COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW.includes(country!)) {
      return isKycDone;
    }

    const isUserNonIndianAndOld = isOldUser(user, isPhoneVerified);
    const isUserNonIndianAndNew = !isUserNonIndianAndOld;

    if (isUserNonIndianAndOld) {
      return isPhoneVerified || proofOfIdentityApproved || proofOfAddressApproved;
    }

    if (isUserNonIndianAndNew) {
      return proofOfIdentityApproved || proofOfAddressApproved;
    }

    return false;
  }
);

const selectGraceDateAfterKycExpiry = createSelector(
  [selectKycExpiryDate],
  kycExpiryDate => {
    if (!kycExpiryDate)
      return { graceDate: null, daysLeft: null, isGracePeriodCrossed: false };
    const graceDate = getDateTime(kycExpiryDate).add(GRACE_EXPIRY_DAYS, 'day');
    const daysLeft = graceDate.diff(timeNow(), 'days', true);
    return {
      graceDate,
      daysLeft,
      isGracePeriodCrossed: graceDate.valueOf() < timeNow().valueOf(),
    };
  }
);

// only for indian exchange
const selectDepositAndWithdrawalVerificationRequired = (state: IState) => {
  const isKycDone = state.user.is_kyc_done;
  const isKycRefreshRequired = state.user.is_kyc_refresh_required;

  return !isKycDone && !isKycRefreshRequired;
};

const selectIsKycRefreshExpired = createSelector(
  [isUserKycDoneSelector, selectIsKycRefreshRequired, selectKycExpiryDate],
  (isKycDone, isKycRefreshRequired, kycExpiryDate) => {
    return checkKycRefreshExpired({
      is_kyc_done: isKycDone,
      is_kyc_refresh_required: isKycRefreshRequired,
      kyc_expiry_date: kycExpiryDate,
    });
  }
);

const selectIsUserInReduceOnlyMode = createSelector(
  [userSelector, selectGraceDateAfterKycExpiry, selectIsKycRefreshExpired],
  (user, { graceDate }, isKycRefreshExpired) => {
    const { is_kyc_refresh_required: isKycRefreshRequired, is_kyc_done: isKycDone } =
      user;

    if (IS_DEMO) return false;

    if (GRACE_KYC_EXPIRY_AVAILABLE) {
      const isKycRefreshGracePeriodExpired =
        graceDate && graceDate.valueOf() < timeNow().valueOf();
      if (
        !isKycDone &&
        isKycRefreshRequired &&
        isKycRefreshExpired &&
        !isKycRefreshGracePeriodExpired
      )
        return true;

      // user is newly unverified or grace period over and trading is restricted
      return false;
    }

    // no grace period here always reduce only after expiry
    return (
      isUserNonIndianAndReduceOnly(user) ||
      isKycRequiredDeadlineCrossed(user) ||
      checkKycRefreshExpired(user)
    );
  }
);

const selectNriStatus = (state: IState) => state.user.nriStatus;

const selectTradePagesVerificationMessageFlag = createSelector(
  [
    isUserKycDoneSelector,
    selectIsUserInReduceOnlyMode,
    selectIsKycRefreshRequired,
    selectIsKycRefreshExpired,
    selectNriStatus,
    isUserVerifiedSelector,
  ],
  (
    isUserKycDone,
    isUserinReduceOnlyMode,
    isKycRefreshRequired,
    isKycRefreshExpired,
    nriStatus,
    isUserVerifiedValue
  ) => {
    if (IS_DEMO) return null;
    if (IS_INDIAN_EXCHANGE) {
      if (nriStatus === NRI_STATUS_VALUES.REDUCE_ONLY)
        return TRADE_PAGE_VERIFICATION_FLAGS.REDUCE_ONLY_NRI;
      if (isUserinReduceOnlyMode) return TRADE_PAGE_VERIFICATION_FLAGS.REDUCE_ONLY;

      // refresh kyc required case
      if (!isUserKycDone && isKycRefreshRequired && isKycRefreshExpired) {
        return TRADE_PAGE_VERIFICATION_FLAGS.RENEW_KYC;
      }

      if (!isUserKycDone && !isKycRefreshRequired) {
        return TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;
      }
    }

    if (isUserinReduceOnlyMode) {
      return TRADE_PAGE_VERIFICATION_FLAGS.REDUCE_ONLY;
    }

    if (!isUserVerifiedValue && !isKycRefreshRequired) {
      return TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;
    }

    return null;
  }
);

const selectUserFirstTradeTime = createSelector([userProfileSelector], profile => {
  return profile?.first_trades_data?.first_trade_time;
});

const selectIsUserSubAccountAndMainAccountDataPresent = (state: IState) => {
  return state.user.is_sub_account && Boolean(state.user.main_account?.email);
};

const selectUserMainAccountEmail = (state: IState) => state.user.main_account?.email;

const selectIsUserBankAccountAdded = (state: IState) => state.user.isBankAccountAdded;

const selectIsAuthChannelConnected = (state: IState) => {
  return state.user.authSocketChannelStatus === AUTH_CHANNEL_STATUS.CONNECTED;
};

const selectIsAuthChannelDisconnected = (state: IState) =>
  state.user.authSocketChannelStatus !== AUTH_CHANNEL_STATUS.CONNECTED;

const selectIsAuthChannelStatus = (state: IState) => {
  return state.user.authSocketChannelStatus;
};

const selectIsAuthChannelFailed = (state: IState) => {
  return [AUTH_CHANNEL_STATUS.ERROR, AUTH_CHANNEL_STATUS.TIMEOUT].includes(
    state.user.authSocketChannelStatus
  );
};

const selectAccountRefreshTriggerLastClicked = (state: IState) =>
  state.user.accountRefreshTriggerLastClicked;

const selectIsDepositBlockedCountry = (state: IState) =>
  DEPOSIT_BLOCKED_COUNTRIES_GLOBAL_EXCHANGE.includes(
    state.user.country?.toLowerCase() ?? ''
  );

const selectIsMiniChartToggleShown = (state: IState) =>
  state.user.showMiniChartToggleShown;

const showAddBankPopupSelector = createSelector([userSelector], user => {
  /**
   * Determines whether to show the "Add Bank Account" popup to the user.
   *
   * Note: trackingInfo.account_mandate_required indicates if source of income submission is pending.
   * - true: Source of income NOT submitted yet
   * - false: Source of income already submitted
   */
  const {
    isBankAccountAdded,
    is_kyc_done: isKycDone,
    tracking_info: trackingInfo,
    occupation: userOccupation,
  } = user;

  /**
   * Normalize occupation:
   * - If user's occupation matches predefined list, use it
   * - If manually typed/custom occupation, default to OTHER
   */
  const occupation = OCCUPATION.includes(userOccupation as string)
    ? userOccupation
    : SOURCE_INCOME_OCCUPATION.OTHER;

  /**
   * Conditional logic based on occupation type:
   *
   * For SALARIED (Private/Public sector):
   *   - Show popup AFTER source of income is submitted (!account_mandate_required)
   *   - These users must complete source of income BEFORE adding bank account
   *
   * For OTHER occupations (Student, Self-employed, etc.):
   *   - Show popup BEFORE source of income is submitted (account_mandate_required)
   *   - These users can add bank account WITHOUT completing source of income first
   */
  const showAddBankPopupCondition = SOURCE_INCOME_OCCUPATION_LIST.includes(
    occupation ?? ''
  )
    ? !trackingInfo?.account_mandate_required
    : trackingInfo?.account_mandate_required;

  // Show popup only if: no bank account added + KYC completed + occupation-specific condition met
  return !isBankAccountAdded && isKycDone && showAddBankPopupCondition;
});

const getUserSubAccountAvailableBalanceInrByUserId = (userId?: number) =>
  createSelector([userSelector], user => {
    if (!userId) return 0;
    const subAccountWallets = user.sub_accounts_wallets || [];
    const userWallet = subAccountWallets.find(wallet => wallet.user_id === userId);
    const currentUserWallets = userWallet?.wallets;
    const availableBalanceInr = currentUserWallets?.reduce((acc, wallet) => {
      return acc + Number(wallet?.available_balance_inr ?? 0);
    }, 0);
    return availableBalanceInr || 0;
  });

const getUserSubAccountAvailableBalanceUsdByUserId = (userId?: number) =>
  createSelector([userSelector], user => {
    if (!userId) return 0;
    const subAccountWallets = user.sub_accounts_wallets || [];
    const userWallet = subAccountWallets.find(wallet => wallet.user_id === userId);
    const currentUserWallets = userWallet?.wallets;
    const availableBalanceUsd = currentUserWallets?.reduce((acc, wallet) => {
      return acc + Number(wallet?.available_balance ?? 0);
    }, 0);
    return availableBalanceUsd || 0;
  });

export {
  isNormalUserSelector,
  isUserLevelOneVerifiedSelector,
  isUserLevelThreeVerifiedSelector,
  isUserLevelTwoVerifiedSelector,
  onlyLevel2VerifiedSelector,
  selectActiveSubAccountEmailEqualsMainAccountEmail,
  selectDepositAndWithdrawalVerificationRequired,
  selectGraceDateAfterKycExpiry,
  selectIsKycRefreshExpired,
  selectIsKycRefreshRequired,
  selectIsUserBankAccountAdded,
  selectIsUserInReduceOnlyMode,
  selectIsUserSubAccount,
  selectIsUserSubAccountAndMainAccountDataPresent,
  selectIsVerifiedIndianUser,
  selectKycExpiryDate,
  selectKycRefreshProcessPending,
  selectMfaCheckForSubAndMainAccount,
  selectShouldAskReActivatedUserToVerifyPhone,
  selectShowDoKycCard,
  selectSignupReferralCode,
  selectTradePagesVerificationMessageFlag,
  selectUserAccountName,
  selectUserEmailSha256,
  selectUserFirstTradeTime,
  selectUserGlobalKycData,
  selectUserMainAccountEmail,
  selectUserPhoneVerificationStatus,
  selectUserPreference,
  selectUserReferralCode,
  selectIsAuthChannelConnected,
  selectIsAuthChannelStatus,
  selectIsAuthChannelFailed,
  selectAccountRefreshTriggerLastClicked,
  selectIsAuthChannelDisconnected,
  selectIsDepositBlockedCountry,
  selectIsMiniChartToggleShown,
  showAddBankPopupSelector,
  selectNriStatus,
  getUserSubAccountAvailableBalanceInrByUserId,
  getUserSubAccountAvailableBalanceUsdByUserId,
};
